import { Component, OnInit } from '@angular/core';
import { MsiContentModalRef, ContentModalModule, TreeviewModule } from '@msi/cobalt';
import { take } from 'rxjs/operators';
import { NodeData, NodeType } from 'src/app/interfaces/node-models.interface';
import { ObjID } from 'src/app/interfaces/object-id.interface';
import { SidetrayDataService } from 'src/app/services/sidetray/sidetray-data.service';
import { getUUID } from 'src/app/utils/id-utils/id.utils';
import { validateWorkspaceTitleInput } from 'src/app/utils/general-functions/general-functions.utils';
import {
	keysAlphabetically,
	nodesAlphabetically,
	stringsAlphabetically,
	valuesAlphabetically,
} from 'src/app/utils/sorters/sorters.utils';
import { Criterion, ProductCriteria, SelectedNode } from 'src/app/interfaces/views.interface';
import { ViewModel, ViewCriteriaModel, ViewCriteriaMetadataModel } from 'src/app/interfaces/views.interface';
import { ViewsService } from 'src/app/services/views/views.service';
import { ObjectSet } from 'src/app/utils/object-set/object-set.utils';
import { SentenceCasePipe } from '../../pipes/sentence-case/sentence-case.pipe';
import { ItemModule } from '@msi/cobalt/item';
import { CheckboxModule } from '@msi/cobalt/checkbox';
import { FormsModule } from '@angular/forms';

import { IconModule } from '@msi/cobalt/icon';

export const criterionSeedFunc = (c: Criterion) => `key={{${c.key}}},product={{${c.product}}},value={{${c.value}}}`;

@Component({
	selector: 'app-view-config-modal',
	templateUrl: './view-config-modal.component.html',
	styleUrls: ['./view-config-modal.component.scss'],
	standalone: true,
	imports: [ContentModalModule, IconModule, FormsModule, CheckboxModule, TreeviewModule, ItemModule, SentenceCasePipe],
})
export class ViewConfigModalComponent implements OnInit {
	nodes: Map<string, NodeData>;
	// Maps Product -> Key -> Value -> (Nodes)
	criteria: Map<ObjID, Map<string, Map<string, Set<string>>>> = new Map();
	numSelectedCriteria = 0;
	isValidTitleAdded = false;
	selectedCriteria: ObjectSet<Criterion> = new ObjectSet(criterionSeedFunc);
	selectedTriggers: SelectedNode[] = [];
	selectedActions: SelectedNode[] = [];
	allSelected = false;

	/** Manages title in modal */
	public title: string;
	public titleInputErrorMessage: string = null;

	/** Current loaded view id if any */
	public viewId: string = null;
	public originalTitle: string;

	products: ProductCriteria[];

	constructor(
		private sidetrayDataService: SidetrayDataService,
		private viewsService: ViewsService,
		public modalRef: MsiContentModalRef
	) {}

	ngOnInit(): void {
		this.sidetrayDataService.sidetrayLoaded$.pipe(take(1)).subscribe(() => {
			this.nodes = new Map(
				[...this.sidetrayDataService.allSidetrayNodes.values()]
					.filter(node => !node.deleted)
					.map(node => [node.vertexId, node])
			);

			const initialSelectedCriteria: ObjectSet<Criterion> = new ObjectSet(criterionSeedFunc);
			this.viewId = this.modalRef?.getData()?.viewId ?? null;

			if (this.viewId) {
				// Get view from ViewsService based on id
				const currentView = this.viewsService.getView(this.viewId);
				this.originalTitle = currentView.name;
				this.title = currentView.name;
				this.titleInputChanged();
				this.allSelected = !currentView.criteria;
				currentView.criteria?.forEach(element => {
					if (!element.metadata) {
						initialSelectedCriteria.add({ product: element.product, key: null, value: null });
					} else {
						element.metadata?.forEach(meta => {
							meta.values?.forEach(value => {
								initialSelectedCriteria.add({ product: element.product, key: meta.key, value });
							});
						});
					}
				});
			}

			// Populate criteria map(s) with the nodes that meet each criteria
			this.nodes.forEach(
				(
					node // Add products
				) => this.criteria.set(node.appId, new Map())
			);
			this.nodes.forEach(
				(
					node // Add keys
				) => node.metadataList?.forEach(kvp => this.criteria.get(node.appId).set(kvp.key, new Map()))
			);
			this.nodes.forEach(
				(
					node // Add values
				) => node.metadataList?.forEach(kvp => this.criteria.get(node.appId).get(kvp.key).set(kvp.value, new Set()))
			);
			this.nodes.forEach(
				(
					node // Add nodeIds
				) =>
					node.metadataList?.forEach(kvp =>
						this.criteria.get(node.appId).get(kvp.key).get(kvp.value).add(node.vertexId)
					)
			);

			// Populate products for display in treeview
			this.products = Array.from(this.criteria).map(x => ({
				productName: this.sidetrayDataService.getProductInfo(x[0])?.displayName ?? x[0],
				productId: x[0],
				checked: false,
				childrenChecked: false,
				keys: Array.from(x[1])
					.map(y => ({
						keyName: y[0],
						values: Array.from(y[1])
							.map(z => ({
								value: z[0],
								checked: false,
							}))
							.sort(valuesAlphabetically),
					}))
					.sort(keysAlphabetically),
			}));

			// Add an entry to criteria for each product which contains all nodes in that product
			Array.from(this.criteria.keys()).forEach(product => {
				this.criteria.get(product).set(
					null,
					new Map([
						[
							null,
							new Set(
								Array.from(this.nodes.values())
									.filter(node => node.appId === product)
									.map(node => node.vertexId)
							),
						],
					])
				);
			});

			// Go through and check the boxes that should be checked
			this.products.forEach(product => {
				if (initialSelectedCriteria.has({ product: product.productId, key: null, value: null })) {
					product.checked = true;
					this.selectProduct(product.productId, true);
				}
				product.keys.forEach(key => {
					key.values.forEach(value => {
						if (
							initialSelectedCriteria.has({
								product: product.productId,
								key: key.keyName,
								value: value.value,
							})
						) {
							value.checked = true;
							this.selectCriteria(product.productId, key.keyName, value.value, true);
						}
					});
				});
			});

			this.updateSelectedNodes();
		});
	}

	cancelClickHandler() {
		this.modalRef.close();
	}

	titleInputChanged() {
		this.titleInputErrorMessage = validateWorkspaceTitleInput(this.title);
		this.isValidTitleAdded = this.titleInputErrorMessage === null;
	}

	saveClickHandler() {
		this.titleInputChanged();

		if (!this.titleInputErrorMessage) {
			// Create view model
			const view = this.createViewModelFromSelectedCriteria(this.viewId);
			this.viewsService.putView(view);
			this.modalRef.close();
		}
	}

	selectAll(checked: boolean) {
		// Make sure it actually changed.
		if (this.allSelected === checked) return;
		this.allSelected = !this.allSelected;
		this.updateSelectedNodes();
	}

	selectProduct(product: string, checked: boolean) {
		this.selectCriteria(product, null, null, checked);
	}

	selectCriteria(product: string, key: string, value: string, checked: boolean) {
		const criterion = {
			product,
			key,
			value,
		};
		if (checked) {
			this.selectedCriteria.add(criterion);
		} else {
			this.selectedCriteria.delete(criterion);
		}

		this.updateSelectedNodes();
		this.updateChildrenSelected();
	}

	/**
	 * Converts criteria into a ViewModel
	 * @returns - view containing the ViewModel?
	 */
	private createViewModelFromSelectedCriteria(id?: string) {
		// If editing - create model with current id
		const currentId = id ?? getUUID();

		// Create view model
		const view: ViewModel = {
			id: currentId,
			name: this.title.replace(/\s+/g, ' ').trim(),
			criteria: [],
			aclUsers: null,
		};

		// If individual items are selected, generate criteria list
		if (!this.allSelected) {
			this.selectedCriteria.forEach(criterion => {
				// Add product if DNE
				if (view.criteria.findIndex(x => x.product === criterion.product) === -1) {
					const viewCriteria: ViewCriteriaModel = {
						product: criterion.product,
						metadata: [],
					};
					view.criteria.push(viewCriteria);
				}
				const viewCriteriaMetadataList = view.criteria.find(x => x.product === criterion.product).metadata;
				// Add key if DNE
				if (viewCriteriaMetadataList.findIndex(x => x.key === criterion.key) === -1) {
					const viewCriteriaMetadata: ViewCriteriaMetadataModel = {
						key: criterion.key,
						values: [],
					};
					viewCriteriaMetadataList.push(viewCriteriaMetadata);
				}
				const viewCriteriaMetadataValues = viewCriteriaMetadataList.find(x => x.key === criterion.key).values;
				// Add value (always DNE at this point)
				viewCriteriaMetadataValues.push(criterion.value);
			});

			view.criteria = view.criteria.map(criteria => ({
				...criteria,
				metadata: criteria.metadata.find(x => x.key === null && x.values[0] === null) ? null : criteria.metadata,
			}));
		} else {
			// "All triggers and actions" is selected. Any selected criteria can be ignored
			view.criteria = null;
		}

		return view;
	}

	// Updates the list of selected nodes (to be displayed in the preview on the right)
	private updateSelectedNodes() {
		let selectedNodes: NodeData[];
		if (!this.allSelected) {
			selectedNodes = Array.from(
				new Set(
					this.selectedCriteria.entries
						.map(x => this.criteria.get(x.product).get(x.key).get(x.value))
						.reduce((a, b) => new Set(Array.from(a).concat(Array.from(b))), new Set())
				)
			)
				.map(id => this.nodes.get(id))
				.sort(nodesAlphabetically);
		} else {
			selectedNodes = Array.from(this.nodes.values());
		}
		const selectedProducts = Array.from(this.selectedCriteria.entries)
			.filter(x => x.key === null && x.value === null)
			.map(x => x.product);

		// Populate the selected metadata for each node (i.e. *why* is this node selected)
		const selectedNodesWithReasons: SelectedNode[] = selectedNodes.map(node => ({
			name: node.friendlyName,
			productName: node.appName,
			type: node.type,
			matchingCriteria: node.metadataList
				?.filter(
					kvp =>
						this.selectedCriteria.has({
							product: node.appId,
							key: kvp.key,
							value: kvp.value,
						}) && !this.allSelected
				)
				.filter(x => !selectedProducts.includes(node.appId) && !!x.value)
				.map(kvp => kvp.value)
				.sort(stringsAlphabetically)
				.join(', '),
		}));

		this.selectedTriggers = selectedNodesWithReasons.filter(node => node.type === NodeType.outputOnly);
		this.selectedActions = selectedNodesWithReasons.filter(node => node.type !== NodeType.outputOnly);

		// Update number of selected criteria, excluding anything under a selected product
		this.numSelectedCriteria = this.allSelected
			? 1
			: this.selectedCriteria.entries.filter(x => !selectedProducts.includes(x.product)).length +
				selectedProducts.length;
	}

	// Mark each product as partially selected if it has sub-items selected (but is not itself selected)
	private updateChildrenSelected() {
		this.products.forEach(product => {
			product.childrenChecked =
				!this.selectedCriteria.has({ product: product.productId, key: null, value: null }) &&
				this.selectedCriteria.entries.some(x => x.product === product.productId);
		});
	}
}

// Copyright (c) Motorola Solutions Inc. All Rights Reserved.
